/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : AiSuggestionDialogContainer.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import CustomDialogBoxV2 from "../common/customDialogBox/CustomDialogBoxV2";
import AISuggestionContent, {
  mapCombinationsToRows,
  mapRecommendationsToRows,
  isRowSelectedInTicket,
} from "./AiSuggestionContext";
import { useDispatch, useSelector } from "react-redux";
import AISuggestionHeader from "./AiSuggestionHeader";
import smartMatchActions from "../../../redux/actions/smart-matching/smartMatchSuggestionActions";
import { HandleApiActions } from "../../../redux/actions";
import helper from "../../../utils/helper";
import AISuggestionWhyDrawer from "./Suggestion-Reasoning/AiSuggestionWhyDrawer";
import { Box, Typography } from "@mui/material";
import CustomButtonV2 from "../common/button/CustomButtonV2";
import CustomToolTip from "../../infoButton/CustomToolTip";
import { toast } from "react-toastify";
import PropTypes from "prop-types";

export default function AISuggestionDialogContainer({
  open,
  onClose,
  entity,
  onLinkRequest,
  onUnlinkRequest,
  closeDialogOnAdd = false,
  selectedComponents = [],
  selectedLegals = [],
  selectedTestCases = [],
  onAddVc,
}) {
  const [status, setStatus] = useState("loading"); // idle | loading | success | empty | error
  const [data, setData] = useState([]);
  const dispatch = useDispatch();
  const selectedProjectDetails = useSelector(
    (state) => state?.projectConfigReducer?.selectedProjectDetails,
  );
  const selectedVariantDetails = useSelector(
    (state) => state?.projectConfigReducer?.selectedVariantDetails,
  );
  const [apiParams, setApiParams] = useState(null);
  const [dialogTitle, setDialogTitle] = useState("AI Suggestions");
  const [whyOpen, setWhyOpen] = useState(false);
  const [selectedRecommendation, setSelectedRecommendation] = useState(null);
  const previousApiParamsRef = useRef(null);
  const [showQueueDialog, setShowQueueDialog] = useState(false);
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(25);
  const [totalSuggestionsCount, setTotalSuggestionsCount] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState(
    "Preparing AI suggestions...",
  );
  const [kgLoading, setKgLoading] = useState(false);
  const [kgLastUpdatedAt, setKgLastUpdatedAt] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState([]);
  const hasAutoTriggeredDeepSearchRef = useRef(false);
  const hasShownDeepEmptyToastRef = useRef(false);
  const isDeepSearchActive = Boolean(apiParams?.deepSearch);
  const requestAbortRef = useRef(null);
  const pendingFinalPayloadRef = useRef(null);
  const loadingMessageQueueRef = useRef([]);
  const loadingMessagePumpRef = useRef(null);

  // Dialog 2 (Combinations) State
  const [combinationDialogOpen, setCombinationDialogOpen] = useState(false);
  const [combinationParentRow, setCombinationParentRow] = useState(null);
  const [combinationRows, setCombinationRows] = useState([]);

  // Dialog 3 (Alternate to Combination) State
  const [alternateDialogOpen, setAlternateDialogOpen] = useState(false);
  const [alternateStatus, setAlternateStatus] = useState("loading");
  const [alternateData, setAlternateData] = useState(null);
  const [alternateLoadingMessage, setAlternateLoadingMessage] = useState(
    "Preparing alternate suggestions...",
  );
  const [selectedAlternateCombinationRow, setSelectedAlternateCombinationRow] =
    useState(null);
  const alternateAbortRef = useRef(null);

  // Dialog 5 (Vehicle Component Suggestions from Vehicle System) State
  const [vcDialogOpen, setVcDialogOpen] = useState(false);
  const [vcStatus, setVcStatus] = useState("loading");
  const [vcData, setVcData] = useState(null);
  const [vcLoadingMessage, setVcLoadingMessage] = useState(
    "Preparing vehicle component suggestions...",
  );
  const [selectedVsRow, setSelectedVsRow] = useState(null);
  const vcAbortRef = useRef(null);

  // Dialog 4 (Preview Selected Components) State
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const allKnownRowsRef = useRef([]);

  const registerRows = useCallback((newRows) => {
    if (!Array.isArray(newRows) || newRows.length === 0) return;
    const existing = allKnownRowsRef.current;
    const existingIds = new Set(existing.map((r) => r.id));
    const toAdd = [];
    for (const r of newRows) {
      if (r && r.id && !existingIds.has(r.id)) {
        existingIds.add(r.id);
        toAdd.push(r);
      }
    }
    if (toAdd.length > 0) {
      allKnownRowsRef.current = [...existing, ...toAdd];
    }
  }, []);

  const ticketContext = useMemo(
    () => ({
      selectedComponents,
      selectedLegals,
      selectedTestCases,
    }),
    [selectedComponents, selectedLegals, selectedTestCases],
  );

  const selectedRows = useMemo(() => {
    const list = [];
    const seen = new Set();
    for (const row of allKnownRowsRef.current) {
      if (isRowSelectedInTicket(row, ticketContext)) {
        const key = row.id;
        if (!seen.has(key)) {
          seen.add(key);
          list.push({ ...row, isLinked: true });
        }
      }
    }
    return list;
  }, [ticketContext, data, combinationRows, alternateData, vcData]);

  const clearLoadingMessagePump = () => {
    loadingMessageQueueRef.current = [];
    pendingFinalPayloadRef.current = null;
    if (loadingMessagePumpRef.current) {
      clearTimeout(loadingMessagePumpRef.current);
      loadingMessagePumpRef.current = null;
    }
  };

  const enqueueLoadingMessage = (msg) => {
    if (!msg) return;
    const queue = loadingMessageQueueRef.current;
    const lastQueued = queue.at(-1) ?? null;
    if (lastQueued === msg) return;
    queue.push(msg);

    if (loadingMessagePumpRef.current) return;

    const pump = () => {
      const next = loadingMessageQueueRef.current.shift();
      if (next) {
        setLoadingMessage(next);
        setLoadingHistory((prev) => [...prev, next]);
        loadingMessagePumpRef.current = setTimeout(pump, 500);
      } else {
        loadingMessagePumpRef.current = null;
        if (pendingFinalPayloadRef.current) {
          const finalPayload = pendingFinalPayloadRef.current;
          pendingFinalPayloadRef.current = null;
          handleFinalSuggestionResponse(finalPayload);
        }
      }
    };

    pump();
  };

  const openWhyThis = (recommendation) => {
    setSelectedRecommendation(recommendation);
    setWhyOpen(true);
  };

  const closeWhyThis = () => {
    setWhyOpen(false);
    setSelectedRecommendation(null);
  };

  const formatKgLastUpdatedAt = (dateValue) => {
    if (!dateValue) return "";

    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return "";

    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    })
      .format(date)
      .replace(/\b(am|pm)\b/i, (period) => period.toUpperCase());
  };

  const handleFinalSuggestionResponse = (resp) => {
    setKgLastUpdatedAt(resp?.kg_last_updated_at || null);

    if (resp?.error) {
      setData([]);
      setTotalSuggestionsCount(0);
      setStatus("error");
    } else if (resp?.queue === true) {
      setShowQueueDialog(true);
      setStatus("idle");
    } else if (!resp?.recommendations || resp.recommendations.length === 0) {
      setTotalSuggestionsCount(
        resp?.totalCount ?? resp?.metadata?.totalCount ?? 0,
      );
      setData([]);
      setStatus("empty");
    } else {
      setTotalSuggestionsCount(
        resp?.totalCount ??
          resp?.metadata?.totalCount ??
          resp?.recommendations?.length ??
          0,
      );
      setData(resp);
      setStatus("success");
    }
  };

  const parseSseChunkLines = (chunkText, onEvent) => {
    const lines = chunkText.split("\n");
    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed.startsWith("data:")) continue;
      const payload = trimmed.slice(5).trim();
      if (!payload) continue;
      try {
        const evt = JSON.parse(payload);
        onEvent(evt);
      } catch (err) {
        console.warn("Failed parsing SSE event payload:", payload, err);
      }
    }
  };

  const processSseStream = async (body) => {
    const reader = body.getReader();
    const decoder = new TextDecoder("utf-8");
    let finalPayload = null;
    let buffered = "";

    const processEvent = (evt) => {
      const stage = evt?.stage ? String(evt.stage).trim() : "";
      const stageLabel = stage && stage !== "VALIDATION" ? `[${stage}] ` : "";
      const rawMessage = evt?.message || "Processing...";
      const cleanMessage = String(rawMessage).replace(/^\[VALIDATION\]\s*/i, "");
      const msg = `${stageLabel}${cleanMessage}`;
      enqueueLoadingMessage(msg);
      if (evt?.stage === "FINAL_RESULT" && evt?.status === "COMPLETED") {
        finalPayload = evt?.payload || null;
      }
    };

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffered += decoder.decode(value, { stream: true });
      const parts = buffered.split("\n\n");
      buffered = parts.pop() || "";

      for (const part of parts) {
        parseSseChunkLines(part, processEvent);
      }
    }

    if (buffered) {
      parseSseChunkLines(buffered, processEvent);
    }

    if (finalPayload) {
      pendingFinalPayloadRef.current = finalPayload;
      if (
        !loadingMessagePumpRef.current &&
        loadingMessageQueueRef.current.length === 0
      ) {
        const readyPayload = pendingFinalPayloadRef.current;
        pendingFinalPayloadRef.current = null;
        handleFinalSuggestionResponse(readyPayload);
      }
    }
  };

  const fetchSuggestions = async (overrideParams = null) => {
    const paramsToSend = overrideParams || apiParams;
    const baseURL = process.env.REACT_APP_BASE_URL || "";
    const endpoint = smartMatchActions?.get_suggestions?.api_name;
    const url = endpoint?.startsWith("http")
      ? endpoint
      : `${baseURL}${endpoint}`;
    const token =
      localStorage.getItem("token") || sessionStorage.getItem("token") || "";

    if (requestAbortRef.current) {
      requestAbortRef.current.abort();
    }
    const controller = new AbortController();
    requestAbortRef.current = controller;
    clearLoadingMessagePump();
    setLoadingHistory([]);
    setLoadingMessage("Starting recommendation pipeline...");

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ ...paramsToSend }),
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new Error(
          `Recommendation API failed with status ${response.status}`,
        );
      }

      const contentType = response.headers.get("content-type") || "";
      const isSse = contentType.includes("text/event-stream");

      if (isSse && response.body) {
        await processSseStream(response.body);
        return;
      }

      const json = await response.json();
      if (json?.stage === "FINAL_RESULT" && json?.payload) {
        handleFinalSuggestionResponse(json.payload);
      } else {
        handleFinalSuggestionResponse(json);
      }
    } catch (err) {
      if (err?.name === "AbortError") return;
      console.error("fetchSuggestions error:", err);
      setData([]);
      setTotalSuggestionsCount(0);
      setStatus("error");
    }
  };

  const fetchAlternateSuggestions = async (payload) => {
    const baseURL = process.env.REACT_APP_BASE_URL || "";
    const endpoint = smartMatchActions?.get_suggestions?.api_name;
    const url = endpoint?.startsWith("http")
      ? endpoint
      : `${baseURL}${endpoint}`;
    const token =
      localStorage.getItem("token") || sessionStorage.getItem("token") || "";

    if (alternateAbortRef.current) {
      alternateAbortRef.current.abort();
    }
    const controller = new AbortController();
    alternateAbortRef.current = controller;

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new Error(
          `Alternate recommendation API failed with status ${response.status}`,
        );
      }

      const contentType = response.headers.get("content-type") || "";
      const isSse = contentType.includes("text/event-stream");

      if (isSse && response.body) {
        const reader = response.body.getReader();
        const decoder = new TextDecoder("utf-8");
        let finalPayload = null;
        let buffered = "";

        const processAltEvent = (evt) => {
          if (evt?.message) {
            setAlternateLoadingMessage(evt.message);
          }
          if (evt?.stage === "FINAL_RESULT" && evt?.status === "COMPLETED") {
            finalPayload = evt?.payload || null;
          }
        };

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffered += decoder.decode(value, { stream: true });
          const parts = buffered.split("\n\n");
          buffered = parts.pop() || "";

          for (const part of parts) {
            parseSseChunkLines(part, processAltEvent);
          }
        }

        if (buffered) {
          parseSseChunkLines(buffered, processAltEvent);
        }

        if (finalPayload?.recommendations?.length) {
          setAlternateData(finalPayload);
          setAlternateStatus("success");
        } else {
          setAlternateData({
            recommendations: [],
            source: payload.source[0],
          });
          setAlternateStatus("empty");
        }
        return;
      }

      const json = await response.json();
      const result =
        json?.stage === "FINAL_RESULT" && json?.payload ? json.payload : json;

      if (result?.recommendations?.length) {
        setAlternateData(result);
        setAlternateStatus("success");
      } else {
        setAlternateData({
          recommendations: [],
          source: payload.source[0],
        });
        setAlternateStatus("empty");
      }
    } catch (err) {
      if (err?.name === "AbortError") return;
      console.error("fetchAlternateSuggestions error:", err);
      setAlternateData(null);
      setAlternateStatus("error");
    }
  };

  const fetchVcSuggestions = async (payload) => {
    const baseURL = process.env.REACT_APP_BASE_URL || "";
    const endpoint = smartMatchActions?.get_suggestions?.api_name;
    const url = endpoint?.startsWith("http")
      ? endpoint
      : `${baseURL}${endpoint}`;
    const token =
      localStorage.getItem("token") || sessionStorage.getItem("token") || "";

    if (vcAbortRef.current) {
      vcAbortRef.current.abort();
    }
    const controller = new AbortController();
    vcAbortRef.current = controller;

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new Error(
          `Vehicle component recommendation API failed with status ${response.status}`,
        );
      }

      const contentType = response.headers.get("content-type") || "";
      const isSse = contentType.includes("text/event-stream");

      if (isSse && response.body) {
        const reader = response.body.getReader();
        const decoder = new TextDecoder("utf-8");
        let finalPayload = null;
        let buffered = "";

        const processVcEvent = (evt) => {
          if (evt?.message) {
            setVcLoadingMessage(evt.message);
          }
          if (evt?.stage === "FINAL_RESULT" && evt?.status === "COMPLETED") {
            finalPayload = evt?.payload || null;
          }
        };

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffered += decoder.decode(value, { stream: true });
          const parts = buffered.split("\n\n");
          buffered = parts.pop() || "";

          for (const part of parts) {
            parseSseChunkLines(part, processVcEvent);
          }
        }

        if (buffered) {
          parseSseChunkLines(buffered, processVcEvent);
        }

        if (finalPayload?.recommendations?.length) {
          setVcData(finalPayload);
          setVcStatus("success");
        } else {
          setVcData({
            recommendations: [],
            source: payload.source[0],
          });
          setVcStatus("empty");
        }
        return;
      }

      const json = await response.json();
      const result =
        json?.stage === "FINAL_RESULT" && json?.payload ? json.payload : json;

      if (result?.recommendations?.length) {
        setVcData(result);
        setVcStatus("success");
      } else {
        setVcData({
          recommendations: [],
          source: payload.source[0],
        });
        setVcStatus("empty");
      }
    } catch (err) {
      if (err?.name === "AbortError") return;
      console.error("fetchVcSuggestions error:", err);
      setVcData(null);
      setVcStatus("error");
    }
  };

  useEffect(() => {
    if (!entity) {
      setApiParams(null);
      setStatus("error");
      return;
    }
    if (!Array.isArray(entity.source) || entity.source.length === 0) {
      console.error("Smart Match requires at least one source entity.");
      setApiParams(null);
      setStatus("error");
      return;
    }
    hasAutoTriggeredDeepSearchRef.current = false;
    hasShownDeepEmptyToastRef.current = false;
    clearLoadingMessagePump();
    setStatus("loading");
    setPage(1);
    setKgLastUpdatedAt(null);
    setLoadingHistory([]);
    setLoadingMessage("Preparing request...");
    const source = entity.source;

    const initialPayload = {
      source,
      intent: "SMART_MATCH",
      scope: entity?.scope || null,
      filters: {
        targetEntityTypes: entity?.targetEntityTypes || [source[0].entityType],
        excludeExistingLinks: true,
      },
      options: {
        topK: 50,
        minConfidence: entity?.minConfidence || 0.1,
        explain: true,
      },
      pageNumber: 1,
      pageSize: rowsPerPage,
      check_queue: true,
    };
    previousApiParamsRef.current = initialPayload;
    setApiParams(initialPayload);
    const titleSuffix = entity?.description
      ? ` for ${helper.toSentenceCase(entity.description)}`
      : "";
    setDialogTitle(`AI Suggestions${titleSuffix}`);
  }, [entity, rowsPerPage]);

  useEffect(() => {
    if (apiParams) {
      fetchSuggestions();
    }
  }, [apiParams]);

  useEffect(() => {
    if (
      status === "empty" &&
      apiParams &&
      !apiParams?.deepSearch &&
      !hasAutoTriggeredDeepSearchRef.current
    ) {
      hasAutoTriggeredDeepSearchRef.current = true;
      toast.success("No suggestions found in fast mode. Trying deep analysis.");
      const deepSearchTimer = setTimeout(() => {
        handleDeepAnalysis();
      }, 3000);

      return () => clearTimeout(deepSearchTimer);
    }
  }, [status, apiParams]);

  useEffect(() => {
    if (
      status === "empty" &&
      apiParams?.deepSearch &&
      !hasShownDeepEmptyToastRef.current
    ) {
      hasShownDeepEmptyToastRef.current = true;
      toast.info("No suggestion found in deep analysis.");
    }
  }, [status, apiParams]);

  useEffect(() => {
    return () => {
      if (requestAbortRef.current) {
        requestAbortRef.current.abort();
        requestAbortRef.current = null;
      }
      if (alternateAbortRef.current) {
        alternateAbortRef.current.abort();
        alternateAbortRef.current = null;
      }
      clearLoadingMessagePump();
    };
  }, []);

  useEffect(() => {
    if (data?.recommendations?.length) {
      const mapped = mapRecommendationsToRows(data.recommendations, data.source);
      registerRows(mapped);
    }
  }, [data, registerRows]);

  useEffect(() => {
    if (combinationRows?.length) {
      registerRows(combinationRows);
    }
  }, [combinationRows, registerRows]);

  useEffect(() => {
    if (alternateData?.recommendations?.length) {
      const mapped = mapRecommendationsToRows(
        alternateData.recommendations,
        alternateData.source,
      );
      registerRows(mapped);
    }
  }, [alternateData, registerRows]);

  useEffect(() => {
    if (vcData?.recommendations?.length) {
      const mapped = mapRecommendationsToRows(
        vcData.recommendations,
        vcData.source,
      );
      registerRows(mapped);
    }
  }, [vcData, registerRows]);

  const handleCloseCombinationDialog = () => {
    setCombinationDialogOpen(false);
    setCombinationParentRow(null);
    setCombinationRows([]);
  };

  const handleCloseAlternateDialog = () => {
    if (alternateAbortRef.current) {
      alternateAbortRef.current.abort();
      alternateAbortRef.current = null;
    }
    setAlternateDialogOpen(false);
    setSelectedAlternateCombinationRow(null);
    setAlternateData(null);
    setAlternateStatus("loading");
  };

  const handleCloseVcDialog = () => {
    if (vcAbortRef.current) {
      vcAbortRef.current.abort();
      vcAbortRef.current = null;
    }
    setVcDialogOpen(false);
    setSelectedVsRow(null);
    setVcData(null);
    setVcStatus("loading");
  };

  const handleOpenPreviewDialog = () => {
    setPreviewDialogOpen(true);
  };

  const handleClosePreviewDialog = () => {
    setPreviewDialogOpen(false);
  };

  const handleRowLink = (row, payload) => {
    if (row) {
      registerRows([row]);
    }
  };

  const handleRowUnlink = (row, payload) => {
    // Unlinking is handled via onUnlinkRequest which updates the ticket state
  };

  const handleDialogClose = () => {
    dispatch({
      type: smartMatchActions.clear_suggestions.action_type,
    });

    setData([]);
    setStatus("loading");
    setApiParams(null);
    setKgLastUpdatedAt(null);
    setShowQueueDialog(false);
    setPage(1);
    setTotalSuggestionsCount(0);
    hasAutoTriggeredDeepSearchRef.current = false;
    hasShownDeepEmptyToastRef.current = false;
    handleCloseCombinationDialog();
    handleCloseAlternateDialog();
    handleClosePreviewDialog();
    handleCloseVcDialog();
    clearLoadingMessagePump();
    if (requestAbortRef.current) {
      requestAbortRef.current.abort();
      requestAbortRef.current = null;
    }

    onClose();
  };

  const handleOpenCombinationDialog = (combinations, row) => {
    setCombinationParentRow(row);
    const rows = mapCombinationsToRows(combinations, row, data?.source);
    setCombinationRows(rows);
    setCombinationDialogOpen(true);
  };

  const handleOpenAlternateDialog = (combinationRow) => {
    setSelectedAlternateCombinationRow(combinationRow);
    setAlternateDialogOpen(true);
    setAlternateStatus("loading");
    setAlternateLoadingMessage(
      "Starting recommendation pipeline for alternate suggestions...",
    );

    const clickedEntityId =
      combinationRow?.raw?.targetContext?.entityId ??
      combinationRow?.raw?.target?.entityId ??
      combinationRow?.id;
    const clickedEntityType =
      combinationRow?.raw?.targetContext?.entityType ??
      combinationRow?.raw?.target?.entityType ??
      "LEGAL_REQUIREMENT";

    const alternatePayload = {
      source: [
        {
          entityType: clickedEntityType,
          entityId: String(clickedEntityId),
        },
      ],
      intent: "SMART_MATCH",
      scope: {
        projectId: Number(
          entity?.scope?.projectId ||
            selectedProjectDetails?.project_id ||
            apiParams?.scope?.projectId ||
            126,
        ),
        variantId: Number(
          entity?.scope?.variantId ||
            selectedVariantDetails?.variant_id ||
            apiParams?.scope?.variantId ||
            146,
        ),
      },
      filters: {
        targetEntityTypes: [clickedEntityType],
        excludeExistingLinks: true,
      },
      options: {
        topK: 50,
        minConfidence: 0.1,
        explain: true,
      },
      pageNumber: 1,
      pageSize: 25,
      check_queue: false,
      deepSearch: true,
    };

    console.log(
      "Hitting recommendation API for Alternate Suggestion:",
      alternatePayload,
    );
    void fetchAlternateSuggestions(alternatePayload);
  };

  const handleOpenVcDialog = (vsId, vsRow) => {
    if (onAddVc) {
      onAddVc(vsId, vsRow);
    }

    setSelectedVsRow(vsRow);
    setVcDialogOpen(true);
    setVcStatus("loading");
    setVcLoadingMessage(
      "Starting recommendation pipeline for vehicle component suggestions...",
    );

    const vsNumericId = !isNaN(Number(vsId)) ? Number(vsId) : vsId;

    const legalsList = Array.isArray(selectedLegals)
      ? selectedLegals
      : selectedLegals
      ? [selectedLegals]
      : [];

    const legalSourcesFromProp = legalsList
      .map((lr) => ({
        entityType: "LEGAL_REQUIREMENT",
        entityId: String(lr?.lr_id ?? lr?.id ?? ""),
      }))
      .filter((s) => s.entityId && s.entityId !== "undefined" && s.entityId !== "null");

    const legalSourcesFromEntity = (entity?.source || []).filter(
      (s) =>
        s?.entityType === "LEGAL_REQUIREMENT" ||
        s?.entityType === "LEGAL",
    );

    const source =
      legalSourcesFromProp.length > 0
        ? legalSourcesFromProp
        : legalSourcesFromEntity.length > 0
        ? legalSourcesFromEntity
        : entity?.source || [];

    const projectId = Number(
      selectedProjectDetails?.project_id ||
        entity?.scope?.projectId ||
        apiParams?.scope?.projectId ||
        126,
    );
    const variantId = Number(
      selectedVariantDetails?.variant_id ||
        entity?.scope?.variantId ||
        apiParams?.scope?.variantId ||
        146,
    );

    const vcPayload = {
      source,
      intent: "SMART_MATCH",
      scope: {
        projectId,
        variantId,
      },
      filters: {
        targetEntityTypes: ["VC"],
        parentEntity: [
          {
            parentEntityType: "VS",
            parentEntityId: vsNumericId,
          },
        ],
        excludeExistingLinks: true,
      },
      options: {
        topK: 50,
        minConfidence: 0.1,
        explain: true,
      },
      pageNumber: 1,
      pageSize: 25,
      check_queue: false,
    };

    console.log(
      "Hitting recommendation API for Vehicle Component Suggestion:",
      vcPayload,
    );
    void fetchVcSuggestions(vcPayload);
  };

  const handleVcLinkRequest = (payload) => {
    const parentVsId =
      payload?.pr_vs_id ||
      selectedVsRow?.raw?.targetContext?.entityId ||
      selectedVsRow?.raw?.target?.entityId ||
      selectedVsRow?.raw?.entityId ||
      selectedVsRow?.vs_id ||
      selectedVsRow?.id;

    const enriched = {
      ...payload,
      type: "VC",
      pr_vs_id: payload?.pr_vs_id || parentVsId,
      parent_vs_id: payload?.parent_vs_id || parentVsId,
    };

    if (onLinkRequest) {
      onLinkRequest(enriched);
    }
  };

  const handleQueueDialogYes = () => {
    setShowQueueDialog(false);
    setStatus("loading");
    const updatedParams = { ...apiParams, check_queue: false };
    setApiParams(updatedParams);
  };

  const handleQueueDialogNo = () => {
    setShowQueueDialog(false);
    handleDialogClose();
  };

  const handleDeepAnalysis = () => {
    if (!apiParams) return;
    setStatus("loading");
    setPage(1);
    const updatedParams = {
      ...apiParams,
      deepSearch: true,
      pageNumber: 1,
      pageSize: rowsPerPage,
    };
    setApiParams(updatedParams);
  };

  const refreshRecommendations = () => {
    if (!apiParams) return;

    setStatus("loading");
    setPage(1);
    setApiParams((prev) =>
      prev
        ? {
          ...prev,
          pageNumber: 1,
          pageSize: rowsPerPage,
          check_queue: false,
        }
        : prev,
    );
  };

  const pollJobStatus = (jobIdStr) => {
    dispatch(
      HandleApiActions({
        https_method: "GET",
        ...smartMatchActions?.get_live_stream_queue,
        api_name: `/smartMatch/api/v1/kg/queue/stream/${jobIdStr}`,
        params: {},
        show_toast: false,
        ignore_app_loading_spinner: true,
      }),
    )
      .then((streamResp) => {
        let state = null;

        if (typeof streamResp === "string") {
          const lines = streamResp
            .split("\n")
            .filter((line) => line.trim());
          const lastLine = lines.at(-1);

          if (lastLine?.startsWith("data:")) {
            try {
              const jsonStr = lastLine.replace(/^data:/, "").trim();
              const parsed = JSON.parse(jsonStr);
              state = parsed.state;
            } catch {
              state = null;
            }
          }
        } else {
          state = streamResp?.state || streamResp?.data?.state;
        }

        if (state === "COMPLETED") {
          toast.success("Knowledge Graph Build Completed.");
          setKgLoading(false);
          refreshRecommendations();
        } else if (state === "RUNNING" || state === "QUEUED") {
          setTimeout(() => pollJobStatus(jobIdStr), 2000);
        } else {
          setKgLoading(false);
        }
      })
      .catch((err) => {
        console.error("pollJobStatus error:", err);
        setKgLoading(false);
      });
  };

  const handleKgBulkRefresh = () => {
    if (kgLoading) return { skipped: true };
    setKgLoading(true);

    dispatch(
      HandleApiActions({
        https_method: "POST",
        ...smartMatchActions?.refresh_kg_nodes,
        params: {},
        show_toast: false,
        ignore_app_loading_spinner: true,
      }),
    )
      .then((resp) => {
        toast.success(resp?.message);
        if (resp?.jobId) {
          const jobIdStr =
            typeof resp.jobId === "object"
              ? JSON.stringify(resp.jobId)
              : String(resp.jobId);
          pollJobStatus(jobIdStr);
        } else {
          setKgLoading(false);
          refreshRecommendations();
        }
      })
      .catch((err) => {
        console.error("handleKgBulkRefresh error:", err);
        setKgLoading(false);
      });
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    setStatus("loading");
    setApiParams((prev) => ({
      ...prev,
      pageNumber: newPage,
      pageSize: rowsPerPage,
      check_queue: false,
    }));
  };

  return (
    <>
      {/* Dialog 1: Regular AI Suggestions Dialog */}
      <CustomDialogBoxV2
        open={open && !showQueueDialog}
        onClose={handleDialogClose}
        dialogRadius="0rem"
        headerSlot={
          <AISuggestionHeader
            onClose={handleDialogClose}
            title={<CustomToolTip length={70}>{dialogTitle}</CustomToolTip>}
            subtitle={
              kgLastUpdatedAt
                ? `Last Refreshed: ${formatKgLastUpdatedAt(kgLastUpdatedAt)}`
                : ""
            }
          />
        }
        hideDefaultActions={status === "loading"}
        requiresDefaultPaddingForContent={false}
        dialogActions={
          <Box
            sx={{
              display: "flex",
              width: "100%",
              justifyContent: "space-between",
            }}
          >
            <Box
              sx={{
                display: "flex",
                gap: 1,
              }}
            >
              <CustomButtonV2
                size="small"
                type="other"
                btnName="Deep Analysis"
                handleOnClick={handleDeepAnalysis}
                disabled={isDeepSearchActive}
                btnStyle={{
                  backgroundColor: "#0F81AF",
                  color: "#FFFFFF",
                  border: "none",
                  fontSize: "14px",
                  fontWeight: 400,
                  "&:hover": {
                    backgroundColor: "#0C6F97",
                    border: "none",
                  },
                  "&.Mui-disabled": {
                    backgroundColor: "#7FB8CF",
                    color: "#FFFFFF",
                    border: "none",
                  },
                }}
              />
              <CustomButtonV2
                type="other"
                size="small"
                btnName={kgLoading ? "Refreshing…" : "Refresh AI Suggestions"}
                handleOnClick={handleKgBulkRefresh}
                disabled={kgLoading}
              />
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              <CustomButtonV2
                size="small"
                type="cancel"
                btnName="Preview"
                disabled={status === "loading" || kgLoading}
                handleOnClick={handleOpenPreviewDialog}
              />
              <CustomButtonV2
                size="small"
                type="cancel"
                btnName="Close"
                handleOnClick={handleDialogClose}
              />
            </Box>
          </Box>
        }
      >
        <AISuggestionContent
          mode="regular"
          status={status}
          data={data}
          loadingMessage={loadingMessage}
          loadingHistory={loadingHistory}
          page={page}
          rowsPerPage={rowsPerPage}
          totalItems={totalSuggestionsCount}
          onPageChange={handlePageChange}
          onWhyThis={openWhyThis}
          onLinkRequest={onLinkRequest}
          onUnlinkRequest={onUnlinkRequest}
          onRowLink={handleRowLink}
          onRowUnlink={handleRowUnlink}
          selectedRows={selectedRows}
          selectedComponents={selectedComponents}
          selectedLegals={selectedLegals}
          selectedTestCases={selectedTestCases}
          onClose={handleDialogClose}
          closeDialogOnAdd={closeDialogOnAdd}
          onOpenCombination={handleOpenCombinationDialog}
          onAddVc={handleOpenVcDialog}
        />
      </CustomDialogBoxV2>

      {/* Dialog 2: Combination Suggestions Dialog */}
      <CustomDialogBoxV2
        open={combinationDialogOpen}
        onClose={handleCloseCombinationDialog}
        dialogRadius="0rem"
        headerSlot={
          <AISuggestionHeader
            onClose={handleCloseCombinationDialog}
            title={
              <CustomToolTip length={70}>
                Combination Suggestions
              </CustomToolTip>
            }
            subtitle={
              combinationParentRow?.component?.number
                ? `Combinations for ${combinationParentRow.component.number}`
                : ""
            }
          />
        }
        requiresDefaultPaddingForContent={false}
        dialogActions={
          <Box
            sx={{
              display: "flex",
              width: "100%",
              justifyContent: "flex-end",
            }}
          >
            <CustomButtonV2
              size="small"
              type="cancel"
              btnName="Close"
              handleOnClick={handleCloseCombinationDialog}
            />
          </Box>
        }
      >
        <AISuggestionContent
          mode="combination"
          status="success"
          rows={combinationRows}
          selectedRows={selectedRows}
          selectedComponents={selectedComponents}
          selectedLegals={selectedLegals}
          selectedTestCases={selectedTestCases}
          onWhyThis={openWhyThis}
          onLinkRequest={onLinkRequest}
          onUnlinkRequest={onUnlinkRequest}
          onRowLink={handleRowLink}
          onRowUnlink={handleRowUnlink}
          onClose={handleCloseCombinationDialog}
          closeDialogOnAdd={closeDialogOnAdd}
          onOpenAlternate={handleOpenAlternateDialog}
        />
      </CustomDialogBoxV2>

      {/* Dialog 3: Alternate Suggestions Dialog (Triggered from Combination) */}
      <CustomDialogBoxV2
        open={alternateDialogOpen}
        onClose={handleCloseAlternateDialog}
        dialogRadius="0rem"
        headerSlot={
          <AISuggestionHeader
            onClose={handleCloseAlternateDialog}
            title={
              <CustomToolTip length={70}>
                Alternate Suggestions
              </CustomToolTip>
            }
            subtitle={
              selectedAlternateCombinationRow?.component?.number
                ? `Alternate Suggestions for ${selectedAlternateCombinationRow.component.number}`
                : ""
            }
          />
        }
        hideDefaultActions={alternateStatus === "loading"}
        requiresDefaultPaddingForContent={false}
        dialogActions={
          <Box
            sx={{
              display: "flex",
              width: "100%",
              justifyContent: "flex-end",
            }}
          >
            <CustomButtonV2
              size="small"
              type="cancel"
              btnName="Close"
              handleOnClick={handleCloseAlternateDialog}
            />
          </Box>
        }
      >
        <AISuggestionContent
          mode="alternate"
          status={alternateStatus}
          data={alternateData}
          loadingMessage={alternateLoadingMessage}
          page={1}
          rowsPerPage={25}
          totalItems={alternateData?.recommendations?.length || 0}
          selectedRows={selectedRows}
          selectedComponents={selectedComponents}
          selectedLegals={selectedLegals}
          selectedTestCases={selectedTestCases}
          onWhyThis={openWhyThis}
          onLinkRequest={onLinkRequest}
          onUnlinkRequest={onUnlinkRequest}
          onRowLink={handleRowLink}
          onRowUnlink={handleRowUnlink}
          onClose={handleCloseAlternateDialog}
          closeDialogOnAdd={closeDialogOnAdd}
        />
      </CustomDialogBoxV2>

      {/* Dialog 4: Preview Selected Suggestions Dialog */}
      <CustomDialogBoxV2
        open={previewDialogOpen}
        onClose={handleClosePreviewDialog}
        dialogRadius="0rem"
        headerSlot={
          <AISuggestionHeader
            onClose={handleClosePreviewDialog}
            title={
              <CustomToolTip length={70}>
                Selected Components Preview
              </CustomToolTip>
            }
            subtitle={
              selectedRows.length > 0
                ? `${selectedRows.length} component${selectedRows.length > 1 ? "s" : ""} selected`
                : ""
            }
          />
        }
        requiresDefaultPaddingForContent={false}
        dialogActions={
          <Box
            sx={{
              display: "flex",
              width: "100%",
              justifyContent: "flex-end",
            }}
          >
            <CustomButtonV2
              size="small"
              type="cancel"
              btnName="Close"
              handleOnClick={handleClosePreviewDialog}
            />
          </Box>
        }
      >
        <AISuggestionContent
          mode="preview"
          status="success"
          rows={selectedRows}
          selectedRows={selectedRows}
          selectedComponents={selectedComponents}
          selectedLegals={selectedLegals}
          selectedTestCases={selectedTestCases}
          onWhyThis={openWhyThis}
          onLinkRequest={onLinkRequest}
          onUnlinkRequest={onUnlinkRequest}
          onRowLink={handleRowLink}
          onRowUnlink={handleRowUnlink}
          onClose={handleClosePreviewDialog}
          closeDialogOnAdd={closeDialogOnAdd}
        />
      </CustomDialogBoxV2>

      {/* Dialog 5: Vehicle Component Suggestions Dialog (Triggered from Add VC on Vehicle System) */}
      <CustomDialogBoxV2
        open={vcDialogOpen}
        onClose={handleCloseVcDialog}
        dialogRadius="0rem"
        headerSlot={
          <AISuggestionHeader
            onClose={handleCloseVcDialog}
            title={
              <CustomToolTip length={70}>
                Vehicle Component Suggestions
              </CustomToolTip>
            }
            subtitle={
              selectedVsRow?.component?.number
                ? `Vehicle Component Suggestions for ${selectedVsRow.component.number}`
                : "Vehicle Component Suggestions"
            }
          />
        }
        hideDefaultActions={vcStatus === "loading"}
        requiresDefaultPaddingForContent={false}
        dialogActions={
          <Box
            sx={{
              display: "flex",
              width: "100%",
              justifyContent: "flex-end",
            }}
          >
            <CustomButtonV2
              size="small"
              type="cancel"
              btnName="Close"
              handleOnClick={handleCloseVcDialog}
            />
          </Box>
        }
      >
        <AISuggestionContent
          mode="regular"
          status={vcStatus}
          data={vcData}
          loadingMessage={vcLoadingMessage}
          page={1}
          rowsPerPage={25}
          totalItems={vcData?.recommendations?.length || 0}
          selectedRows={selectedRows}
          selectedComponents={selectedComponents}
          selectedLegals={selectedLegals}
          selectedTestCases={selectedTestCases}
          onWhyThis={openWhyThis}
          onLinkRequest={handleVcLinkRequest}
          onUnlinkRequest={onUnlinkRequest}
          onRowLink={handleRowLink}
          onRowUnlink={handleRowUnlink}
          onClose={handleCloseVcDialog}
        />
      </CustomDialogBoxV2>

      {/* Reason Drawer for Why This */}
      <AISuggestionWhyDrawer
        open={whyOpen}
        onClose={closeWhyThis}
        recommendation={selectedRecommendation}
      />

      {/* Knowledge Graph Progress Dialog */}
      <CustomDialogBoxV2
        open={showQueueDialog}
        onClose={handleQueueDialogNo}
        title="Knowledge Graph"
        actionButtonStyle={"continuous"}
        successButtonName={"Yes"}
        rejectButtonName={"No"}
        onClickSuccess={handleQueueDialogYes}
        onClickReject={handleQueueDialogNo}
      >
        <Box sx={{ p: 2 }}>
          <Typography>
            Knowledge Graph build in progress. Do you want to see existing
            suggestions?
          </Typography>
        </Box>
      </CustomDialogBoxV2>
    </>
  );
}

AISuggestionDialogContainer.propTypes = {
  open: PropTypes.bool.isRequired,
  entity: PropTypes.shape({
    source: PropTypes.arrayOf(
      PropTypes.shape({
        entityType: PropTypes.string.isRequired,
        entityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
          .isRequired,
      }),
    ).isRequired,
    description: PropTypes.string,
    scope: PropTypes.shape({
      projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      variantId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
    targetEntityTypes: PropTypes.arrayOf(PropTypes.string),
    topK: PropTypes.number,
    minConfidence: PropTypes.number,
  }),
  onClose: PropTypes.func.isRequired,
  onLinkRequest: PropTypes.func.isRequired,
  onUnlinkRequest: PropTypes.func.isRequired,
  closeDialogOnAdd: PropTypes.bool,
  onAddVc: PropTypes.func,
};
